import { Component, ChangeDetectionStrategy, OnInit, ChangeDetectorRef, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CalendarEvent } from '../../../models/calendar-event.interface';
import { addDays, isBefore, isSameDay, startOfMonth, endOfMonth, isAfter, isEqual, isWithinInterval, eachDayOfInterval } from 'date-fns';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { CalendarModule } from 'primeng/calendar';
import { DropdownModule } from 'primeng/dropdown';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { AdditionalInformation, AdditionalInformationDescription } from '../../../models/enums/additionalInformation';
import { ActivatedRoute } from '@angular/router';
import { AdditionalInformationService } from '../../../services/additional-information/additional-information.service';
import { MessageService } from 'primeng/api';

interface CalendarDay {
  date: Date;
  events: CalendarEvent[];
  isPast: boolean;
  isToday: boolean;
  isCurrentMonth: boolean;
}

interface TaskType {
  type: AdditionalInformation;
  selected?: boolean;
}


// Extended interface for tasks with all properties used in the template
interface ExtendedTask extends CalendarEvent {
  taskType: 'single';
  position?: number;
}

@Component({
  selector: 'app-calendar-view',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonModule, DialogModule, CalendarModule, DropdownModule, ProgressSpinnerModule],
  templateUrl: './calendar-view.component.html',
  styleUrl: './calendar-view.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CalendarViewComponent implements OnInit {

  @Input() userId: string = '';
  @Input() mentorId: string = '';
  @Input() isMentorView: boolean = false;

  today = new Date();
  // Use current month and year
  currentMonth = this.today.getMonth(); // Current month (0-indexed)
  currentYear = this.today.getFullYear();
  selectedDate: Date = new Date(this.currentYear, this.currentMonth, 1);
  isVisible: boolean = false;
  selectedTask: ExtendedTask | null = null;
  selectedCalendarDay: CalendarDay | null = null;
  isLoading: boolean = false;

  @Output() upcomingTasksChange = new EventEmitter<CalendarEvent[]>();

  private _selectedTaskType: AdditionalInformation | null = null;

  @Input()
  set selectedTaskType(value: AdditionalInformation | null) {
    this._selectedTaskType = value;
    if (this.userId) {
      this.loadAllTasks();
    }
  }

  get selectedTaskType(): AdditionalInformation | null {
    return this._selectedTaskType;
  }

  // Task type configurations for Dropdown
  taskTypeOptions = [
    { label: 'All Tasks', value: null },
    { label: 'General', value: AdditionalInformation.General },
    { label: 'Profile', value: AdditionalInformation.Profile },
    { label: 'Document', value: AdditionalInformation.Document },
    { label: 'Check List', value: AdditionalInformation.CheckList },
    { label: 'EssayPilot', value: AdditionalInformation.EssayPilot }
  ];

  events: CalendarEvent[] = [];
  private _calendarDays: CalendarDay[] = [];
  pendingSelectDate: Date | null = null;

  constructor(
    private route: ActivatedRoute,
    private additionalInformationService: AdditionalInformationService,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    if (!this.userId) {
      this.userId = this.route.snapshot.paramMap.get('id') || '';
    }
    // Also try to load from API
    if (this.userId) {
      this.loadAllTasks();
    }
  }

  loadAllTasks(): void {
    this.isLoading = true;
    this.cdr.markForCheck();

    // Determine which types to fetch
    let typesToFetch: AdditionalInformation[] = [];
    if (this.selectedTaskType !== null) {
      typesToFetch = [this.selectedTaskType];
    } else {
      typesToFetch = [
        AdditionalInformation.General,
        AdditionalInformation.Profile,
        AdditionalInformation.Document,
        AdditionalInformation.CheckList,
        AdditionalInformation.EssayPilot
      ];
    }

    // Make a single API call with all selected types
    this.additionalInformationService.getAllAdditionalInformationByTypes({
      studentId: this.userId,
      mentorId: this.mentorId,
      types: typesToFetch,
      monthYear: `${(this.selectedDate.getMonth() + 1).toString().padStart(2, '0')}/${this.selectedDate.getFullYear()}`
    }).subscribe({
      next: (response) => {
        this.events = [];

        // Try different possible response structures
        let taskDetails = [];
        if (response.additionalInfo) {
          taskDetails = response.additionalInfo;
        } else if ((response as any).result && Array.isArray((response as any).result)) {
          taskDetails = (response as any).result;
        } else if (Array.isArray(response)) {
          taskDetails = response;
        }
        const typeEvents = taskDetails.map((info: any) => {
          // Parse dates properly
          const startDate = info.startDate ? new Date(info.startDate) : (info.createdOn ? new Date(info.createdOn) : new Date());
          const endDate = info.dueDate ? new Date(info.dueDate) : startDate;

          // Generate colors based on colourCode from API
          const colourCode = info.colourCode || '#2738b7';
          const backgroundColor = colourCode; // Use original colourCode as background
          const textColor = this.generateTextColor(colourCode);

          return {
            id: info.id,
            name: info.question,
            date: endDate, // Keep for backward compatibility
            startDate: startDate,
            endDate: endDate,
            question: info.question,
            addedOn: info.createdOn ? new Date(info.createdOn) : new Date(),
            dueDate: endDate,
            type: typeof info.type === 'string' ? parseInt(info.type) : info.type,
            typeName: AdditionalInformationDescription[(typeof info.type === 'string' ? parseInt(info.type) : info.type) as AdditionalInformation] || 'General',
            color: textColor,
            backgroundColor: backgroundColor,
            colourCode: colourCode, // Store original colour code
            status: info.status || info.reply != null ? 'submitted' : 'Pending',
            studentId: info.studentId,
            mentorId: info.mentorId,
            reply: info.reply,
            attachment: info.attachment,
            fileType: info.fileType,
            requestedAttachment: info.requestedAttachment,
            createdOn: info.createdOn,
            updatedOn: info.updatedOn
          };
        });

        this.events.push(...typeEvents);
        this.updateCalendarDays();
        
        // Emit upcoming tasks (pending tasks)
        const pendingTasks = this.events.filter(e => e.status === 'Pending');
        this.upcomingTasksChange.emit(pendingTasks);

        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.isLoading = false;
        this.cdr.markForCheck();
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error fetching task information'
        });
      }
    });
  }

  // Update calendar days when events change
  private updateCalendarDays(): void {
    const start = startOfMonth(new Date(this.currentYear, this.currentMonth));
    const end = endOfMonth(start);
    const days: CalendarDay[] = [];

    // Calculate the first day to display (start of week, Sunday)
    const firstDayOfWeek = start.getDay();
    let gridStart = addDays(start, -firstDayOfWeek);

    // Calculate the last day to display (end of week, Saturday)
    const lastDayOfWeek = end.getDay();
    let gridEnd = addDays(end, 6 - lastDayOfWeek);

    for (let d = gridStart; d <= gridEnd; d = addDays(d, 1)) {
      const dayEvents = this.events?.filter(e => {
        const eventEndDate = new Date(e.endDate || e.date);
        const currentDate = d;

        // Show tasks only on their end date
        return isSameDay(currentDate, eventEndDate);
      });

      days.push({
        date: d,
        events: dayEvents ?? [],
        isPast: isBefore(d, this.today),
        isToday: isSameDay(d, this.today),
        isCurrentMonth: d.getMonth() === this.currentMonth
      });
    }
    this._calendarDays = days;

    // Check if there is a pending date selection
    if (this.pendingSelectDate) {
      const day = this._calendarDays.find(d => isSameDay(d.date, this.pendingSelectDate!));
      if (day) {
        this.selectedCalendarDay = day;
      }
      this.pendingSelectDate = null;
    }
  }

  selectDate(date: Date): void {
    const targetDate = new Date(date);
    this.currentMonth = targetDate.getMonth();
    this.currentYear = targetDate.getFullYear();
    this.selectedDate = new Date(this.currentYear, this.currentMonth, 1);
    
    // Set pending select date so updateCalendarDays can apply it
    this.pendingSelectDate = targetDate;
    
    // Trigger task reloading for the selected month/year
    this.loadAllTasks();
  }

  get calendarDays(): CalendarDay[] {
    if (this._calendarDays.length === 0) {
      this.updateCalendarDays();
    }
    return this._calendarDays;
  }

  // Generate text color based on background brightness
  private generateTextColor(colourCode: string): string {
    const hex = colourCode.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);

    // Calculate brightness
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 128 ? '#212022' : '#ffffff';
  }

  onTaskTypeChange(): void {
    // Reload tasks when task type selection changes
    this.loadAllTasks();
  }

  onDayClick(day: CalendarDay): void {
    this.selectedCalendarDay = day;
  }

  get daysOfWeek(): string[] {
    return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  }


  // Get all tasks for a specific day - show all tasks on their end date
  getAllTasksForDay(day: CalendarDay, dayIndex: number): any[] {
    const allTasks: any[] = [];

    // Get all events for this day (both single-day and multi-day)
    day.events.forEach((event, index) => {
      allTasks.push({
        ...event,
        taskType: 'single',
        position: index
      });
    });

    return allTasks;
  }


  prevMonth(): void {
    if (this.currentMonth === 0) {
      this.currentMonth = 11;
      this.currentYear--;
    } else {
      this.currentMonth--;
    }
    this.selectedDate = new Date(this.currentYear, this.currentMonth, 1);
    this.loadAllTasks(); // Reload tasks for new month
  }

  nextMonth(): void {
    if (this.currentMonth === 11) {
      this.currentMonth = 0;
      this.currentYear++;
    } else {
      this.currentMonth++;
    }
    this.selectedDate = new Date(this.currentYear, this.currentMonth, 1);
    this.loadAllTasks(); // Reload tasks for new month
  }

  onTaskClick(task: CalendarEvent | ExtendedTask): void {
    // Convert CalendarEvent to ExtendedTask if needed
    if ('taskType' in task) {
      this.selectedTask = task;
    } else {
      // Convert CalendarEvent to ExtendedTask
      this.selectedTask = {
        ...task,
        taskType: 'single'
      };
    }
    this.isVisible = true;
  }

  onCalendarSelect(date: Date): void {
    this.currentMonth = date.getMonth();
    this.currentYear = date.getFullYear();
    this.selectedDate = date;
    this.loadAllTasks(); // Reload tasks for new month
  }

  getTaskStyle(task: CalendarEvent): any {
    const isPast = isBefore(new Date(task.endDate || task.date), this.today);
    const isToday = isSameDay(new Date(task.endDate || task.date), this.today);

    let opacity = 1;
    if (isPast && !isToday) {
      opacity = 0.6;
    }

    return {
      'background-color': task.backgroundColor || '#fffde7',
      'color': task.color || '#212022',
      'border-color': task.borderColor || '#ffe082',
      'opacity': opacity
    };
  }

  getTaskTypeName(type: AdditionalInformation | undefined): string {
    if (type === undefined) return 'Unknown';
    const typeValue = typeof type === 'string' ? parseInt(type) : type;
    return AdditionalInformationDescription[typeValue as AdditionalInformation] || 'Unknown';
  }

  isTaskOverdue(task: CalendarEvent): boolean {
    return task.reply == null && isBefore(new Date(task.endDate || task.date), this.today);
  }

  isTaskDueToday(task: CalendarEvent): boolean {
    return isSameDay(new Date(task.endDate || task.date), this.today);
  }

  isTaskUpcoming(task: CalendarEvent): boolean {
    return isAfter(new Date(task.endDate || task.date), this.today);
  }

  // Make isSameDay available to template
  isSameDay(date1: Date, date2: Date): boolean {
    return isSameDay(date1, date2);
  }

  openFile(file: string | null | undefined) {
    if (file) {
      window.open(file, '_blank');
    }
  }
}
